import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Compass, Footprints, Images, Sparkles, Trophy, Users } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { useCommunity } from "@/context/CommunityContext";
import TopBar from "@/components/TopBar";
import IdeaBlock from "@/components/IdeaBlock";

const PILOT_MODE = process.env.REACT_APP_PILOT_MODE !== "false";

const ENTRIES = [
  { to: "/wege", title: "WEGE ENTDECKEN", subtitle: "Kunst, Geschichte, Märchen, Familie & mehr", icon: Compass, testId: "entry-wege" },
  { to: "/challenges", title: "CHALLENGE MACHEN", subtitle: "Finden, Sport, Foto, Bestleistungen & mehr", icon: Trophy, testId: "entry-challenges" },
  { to: "/spielen", title: "MITEINANDER SPIELEN", subtitle: "Matches, LOSER und Ergebnisse", icon: Users, testId: "entry-spielen" },
];

function cardLink(card) {
  if (card.link_type === "LOVE_TREE") return "/liebesbaum";
  if (card.link_type === "WALK") return `/wege/${card.link_value}`;
  if (card.link_type === "CHALLENGE") return `/challenges/${card.link_value}`;
  if (card.link_type === "GAME") return `/spielen/${card.link_value}`;
  if (card.link_type === "URL" && card.link_value) return card.link_value;
  return "/challenges";
}

function PilotExperienceCard({ walk, index }) {
  const openMuseum = walk.experience_mode === "OPEN_COLLECTION";
  const Icon = openMuseum ? Images : Footprints;
  const number = walk.collectible_number || (openMuseum ? "SR-002" : "SR-001");
  const facts = openMuseum
    ? [
        `${walk.artwork_count ?? 49} Kunstwerke`,
        `${walk.completion_target ?? 6} auswählen`,
        "Liebling wählen",
      ]
    : ["6 Stationen", walk.duration_text || "ca. 60 Minuten", "echte Orte"];

  return (
    <Link
      to={`/wege/${walk.slug || walk.id}`}
      className="card-lift group block overflow-hidden rounded-[1.75rem] border bg-white rise"
      style={{ animationDelay: `${index * 80}ms` }}
      data-testid={`home-experience-${walk.slug || walk.id}`}
    >
      <div className="relative h-52 overflow-hidden bg-[var(--brand-soft)] grain">
        {walk.hero_image ? (
          <img
            src={fileUrl(walk.hero_image)}
            alt=""
            className="h-full w-full object-cover transition duration-700 group-hover:scale-[1.03]"
          />
        ) : null}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/5 to-black/10" />
        <div className="absolute inset-x-0 top-0 flex items-start justify-between gap-3 p-4">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/95 px-3 py-1.5 text-[10px] font-bold tracking-[0.12em] text-[var(--brand)]">
            <Icon className="h-3.5 w-3.5" /> {openMuseum ? "FREIES MUSEUM" : "GEFÜHRTER WEG"}
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--brand)] px-3 py-1.5 text-[10px] font-bold tracking-wide text-white">
            <Sparkles className="h-3.5 w-3.5" /> +{walk.completion_xp ?? 100} XP
          </span>
        </div>
        <div className="absolute inset-x-0 bottom-0 p-5 text-white">
          <p className="text-[10px] font-bold tracking-[0.2em] text-white/75">{number}</p>
          <h3 className="mt-1 font-serif text-3xl leading-none">{walk.title}</h3>
        </div>
      </div>
      <div className="p-5">
        <p className="text-sm leading-relaxed text-[var(--ink-soft)]">
          {openMuseum
            ? "Sieh hin. Finde deinen Blick. Sammle, was dich berührt."
            : (walk.short_description || "Sechs Stationen durch echte Orte in St. Ruprecht.")}
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {facts.filter(Boolean).map((fact) => (
            <span key={fact} className="rounded-full bg-[var(--brand-soft)] px-3 py-1.5 text-[11px] font-semibold text-[var(--brand)]">
              {fact}
            </span>
          ))}
        </div>
        <span className="mt-5 inline-flex items-center gap-2 text-xs font-bold tracking-[0.1em] text-[var(--brand)]">
          {openMuseum ? "KUNSTPARK ÖFFNEN" : "SPUR ÖFFNEN"}
          <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  );
}

export default function Home() {
  const { community, slug, loading } = useCommunity();
  const [home, setHome] = useState(null);

  useEffect(() => {
    if (!slug) return;
    api
      .get(`/communities/${slug}/home`)
      .then(({ data }) => setHome(data))
      .catch(() => setHome(null));
  }, [slug]);

  if (loading) return <div className="p-10 text-center text-sm text-[var(--ink-soft)]">wird geladen …</div>;
  if (!community)
    return (
      <div className="p-10 text-center">
        <p className="text-sm text-[var(--ink-soft)]">Es ist noch keine Gemeinde freigegeben.</p>
        <Link to="/admin" className="btn btn-ghost mt-4">Zum Adminbereich</Link>
      </div>
    );

  const cards = PILOT_MODE ? [] : (home?.feature_cards || []);
  const visibleEntries = PILOT_MODE ? [] : ENTRIES;
  const featuredWalks = home?.featured_walks || [];
  const pilotWalks = [...featuredWalks].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div data-testid="home-page">
      <TopBar />
      <div className="relative overflow-hidden">
        <div className="relative h-[58vh] min-h-[380px] w-full grain">
          {community.hero_image ? (
            <img
              src={fileUrl(community.hero_image)}
              alt=""
              className="absolute inset-0 h-full w-full object-cover object-[50%_30%] sm:object-[50%_32%]"
            />
          ) : (
            <div className="absolute inset-0" style={{ background: "var(--brand)" }} />
          )}
          {PILOT_MODE ? (
            <>
              <div className="absolute inset-0 bg-black/10" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(246,242,233,0.84) 0%, rgba(246,242,233,0.68) 25%, rgba(246,242,233,0.38) 39%, rgba(246,242,233,0.10) 52%, rgba(246,242,233,0) 64%)" }} />
              <div className="absolute inset-0 mx-auto flex max-w-6xl items-center px-6 sm:px-10">
                <div className="w-[78%] max-w-xl rise">
                  <p className="font-serif text-3xl font-semibold tracking-[0.16em] text-[var(--brand)] sm:text-5xl">SPUREN</p>
                  <p className="mt-1 text-[10px] font-bold tracking-[0.22em] text-[var(--brand)] sm:text-sm">ST. RUPRECHT AN DER RAAB</p>
                  <h1 className="mt-7 font-serif text-4xl leading-[1.04] text-[var(--brand)] sm:text-6xl" data-testid="home-community-name">
                    St. Ruprecht<br />neu entdecken.
                  </h1>
                  <p className="mt-4 text-xs font-bold tracking-[0.22em] text-[var(--brand)] sm:text-base">ORTE. MENSCHEN. GESCHICHTEN.</p>
                  <p className="mt-1 text-xs font-bold tracking-[0.18em] text-[var(--brand)] sm:text-sm">EIN SPAZIERGANG MIT MEHR.</p>
                  <Link
                    to="/wege"
                    className="mt-7 inline-flex items-center gap-3 rounded-full bg-[var(--brand)] px-6 py-3 text-sm font-bold tracking-wide text-white shadow-sm transition hover:opacity-90"
                  >
                    <Compass className="h-5 w-5" />
                    SPUREN ENTDECKEN
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(20,26,22,0.15) 0%, rgba(20,26,22,0.72) 68%, rgba(20,26,22,0.92) 100%)" }} />
              <div className="absolute inset-x-0 bottom-0 mx-auto max-w-2xl px-5 pb-8">
                <div className="rise">
                  <p className="eyebrow !text-white/70">{community.subtitle || "entdecken"}</p>
                  <h1 className="mt-1 text-4xl leading-[1.05] text-white sm:text-5xl" data-testid="home-community-name">
                    {community.name}
                  </h1>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="mx-auto max-w-2xl px-5">
        <section className="mt-8 rise">
          <h2 className="text-3xl leading-tight sm:text-4xl">
            {PILOT_MODE ? "Zwei Erlebnisse. Zwei Arten hinzusehen." : `Entdecke ${community.short_name || community.name} auf eine andere Art.`}
          </h2>
          <p className="mt-3 text-base leading-relaxed text-[var(--ink-soft)]">
            {PILOT_MODE
              ? "Folge einer geführten SPUR durch den Ort oder entdecke den Kunstpark als freies Museum. Beide Wege belohnen deinen Blick – mit einer eigenen Sammelkarte und SPUREN-XP."
              : (community.description || "Wege, Geschichten, Kunst, Sport und Begegnung – direkt vor der Haustür.")}
          </p>
        </section>

        {PILOT_MODE ? (
          <section className="mt-8" data-testid="home-experiences">
            <p className="eyebrow">Wähle deine Spur</p>
            <div className="mt-4 grid gap-5 sm:grid-cols-2">
              {pilotWalks.map((walk, index) => (
                <PilotExperienceCard key={walk.id} walk={walk} index={index} />
              ))}
            </div>
            {!pilotWalks.length ? (
              <Link to="/wege" className="btn btn-primary mt-4 w-full">ERLEBNISSE ANSEHEN</Link>
            ) : null}
          </section>
        ) : (
          <section className="mt-8 space-y-3" data-testid="home-entries">
            {visibleEntries.map(({ to, title, subtitle, icon: Icon, testId }, index) => (
              <Link
                key={to}
                to={to}
                data-testid={testId}
                className="card-lift flex items-center gap-4 rounded-2xl border bg-white px-5 py-5 rise"
                style={{ animationDelay: `${index * 70}ms` }}
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl" style={{ background: "var(--brand-soft)" }}>
                  <Icon className="h-5 w-5" style={{ color: "var(--brand)" }} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-bold tracking-wide">{title}</span>
                  <span className="mt-0.5 block text-sm text-[var(--ink-soft)]">{subtitle}</span>
                </span>
                <ArrowRight className="h-5 w-5 shrink-0 opacity-40" />
              </Link>
            ))}
          </section>
        )}

        {cards.length > 0 ? (
          <section className="mt-12" data-testid="home-special">
            <p className="eyebrow">Besonders in {community.short_name || community.name}</p>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              {cards.map((card) => {
                const to = cardLink(card);
                const external = to.startsWith("http");
                const inner = (
                  <>
                    <div className="h-36 w-full overflow-hidden bg-[var(--brand-soft)]">
                      {card.image ? <img src={fileUrl(card.image)} alt="" className="h-full w-full object-cover" /> : null}
                    </div>
                    <div className="p-4">
                      <p className="text-lg">{card.title}</p>
                      {card.subtitle ? <p className="mt-1 text-sm text-[var(--ink-soft)]">{card.subtitle}</p> : null}
                    </div>
                  </>
                );
                return external ? (
                  <a key={card.id} href={to} className="card-lift overflow-hidden rounded-2xl border bg-white" data-testid={`feature-card-${card.id}`}>
                    {inner}
                  </a>
                ) : (
                  <Link key={card.id} to={to} className="card-lift overflow-hidden rounded-2xl border bg-white" data-testid={`feature-card-${card.id}`}>
                    {inner}
                  </Link>
                );
              })}
            </div>
          </section>
        ) : null}

        {!PILOT_MODE && featuredWalks.length ? (
          <section className="mt-12" data-testid="home-new-popular">
            <p className="eyebrow">Neu & beliebt</p>
            <div className="mt-4 space-y-3">
              {featuredWalks.map((walk) => (
                <Link key={walk.id} to={`/wege/${walk.id}`} className="card-lift flex gap-4 overflow-hidden rounded-2xl border bg-white">
                  <div className="h-24 w-28 shrink-0 bg-[var(--brand-soft)]">
                    {walk.hero_image ? <img src={fileUrl(walk.hero_image)} alt="" className="h-full w-full object-cover" /> : null}
                  </div>
                  <div className="min-w-0 flex-1 py-3 pr-4">
                    <p className="truncate text-lg">{walk.title}</p>
                    <p className="mt-0.5 line-clamp-2 text-sm text-[var(--ink-soft)]">{walk.short_description}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        ) : null}

        {!PILOT_MODE ? <IdeaBlock communityId={community.id} /> : null}

        <footer className="mt-12 border-t py-8 text-sm text-[var(--ink-soft)]" data-testid="footer">
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            <Link to="/rechtliches/impressum" className="underline">Impressum</Link>
            <Link to="/rechtliches/datenschutz" className="underline">Datenschutz</Link>
            <Link to="/rechtliches/nutzungsbedingungen" className="underline">Nutzungsbedingungen</Link>
            <Link to="/rechtliches/community-regeln" className="underline">Community-Regeln</Link>
          </div>
          <p className="mt-4">{community.address}</p>
          <Link to="/admin" className="mt-4 inline-block underline" data-testid="footer-admin-link">Adminbereich</Link>
        </footer>
      </div>
    </div>
  );
}
